import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import InputAdornment from '@mui/material/InputAdornment'
import OutlinedInput from '@mui/material/OutlinedInput'
import React, { FC, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import { colors } from '@hazadapt-git/public-core-base'
import { IoCalendarOutline } from 'react-icons/io5'
import {
    format,
    isWithinInterval,
    isSameDay,
    isValid,
    startOfDay,
    subDays,
    endOfDay,
    subWeeks,
    startOfWeek,
    endOfWeek,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfYear,
} from 'date-fns'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateField } from '@mui/x-date-pickers'
import { primaryIconSize } from '../../lib/styles/universal'

export interface DateRangePreset {
    label: string
    getValue: (
        referenceDate: Date,
        orgCreationDate?: Date | string | number | null
    ) => { startDate: Date; endDate: Date }
}

interface DateRangePickerProps {
    orgCreationDate: Date | string | number | null
    startDate: Date | null
    endDate: Date | null
    onChange: (range: { startDate: Date; endDate: Date }) => void
}

interface RangeDayProps extends PickersDayProps<Date> {
    rangeStart: Date | null
    rangeEnd: Date | null
}

const dateRangePresets: DateRangePreset[] = [
    {
        label: 'Yesterday',
        getValue: (referenceDate) => {
            const start = startOfDay(subDays(referenceDate, 1))
            const end = endOfDay(subDays(referenceDate, 1))
            return { startDate: start, endDate: end }
        },
    },
    {
        label: 'Last 7 days',
        getValue: (referenceDate) => {
            const start = startOfDay(subDays(referenceDate, 7))
            const end = endOfDay(subDays(referenceDate, 1))
            return { startDate: start, endDate: end }
        },
    },
    {
        label: 'Last 30 days',
        getValue: (referenceDate) => {
            const start = startOfDay(subDays(referenceDate, 30))
            const end = endOfDay(subDays(referenceDate, 1))
            return { startDate: start, endDate: end }
        },
    },
    {
        label: 'Last 90 days',
        getValue: (referenceDate) => {
            const start = startOfDay(subDays(referenceDate, 90))
            const end = endOfDay(subDays(referenceDate, 1))
            return { startDate: start, endDate: end }
        },
    },
    {
        label: 'Last Week',
        getValue: (referenceDate) => {
            const previousWeek = subWeeks(referenceDate, 1)
            return {
                startDate: startOfDay(startOfWeek(previousWeek)),
                endDate: endOfDay(endOfWeek(previousWeek)),
            }
        },
    },
    {
        label: 'Last Month',
        getValue: (referenceDate) => {
            const previousMonth = subMonths(referenceDate, 1)
            return {
                startDate: startOfDay(startOfMonth(previousMonth)),
                endDate: endOfDay(endOfMonth(previousMonth)),
            }
        },
    },
    {
        label: 'Year to Date',
        getValue: (referenceDate) => {
            const start = startOfDay(startOfYear(referenceDate))
            const end = endOfDay(referenceDate)
            return { startDate: start, endDate: end }
        },
    },
		// Currently we allow customers to access data as far back as May 1, 2023, when we first started collecting data. Eventually we may want to pivot to only allwing them to access as far back as when they created their organization.
    {
			label: 'Lifetime',
			getValue: (referenceDate) => ({
				startDate: startOfDay(new Date(2023, 4, 1)),
				endDate: endOfDay(referenceDate),
			}),
    },
		// Alternative implementation for 'Lifetime' range based on organization creation date.
		// {
		// 		label: 'Lifetime',
		// 		getValue: (referenceDate, orgCreationDate) => {
		// 				const parsedCreationDate =
		// 						orgCreationDate instanceof Date
		// 								? orgCreationDate
		// 								: orgCreationDate == null
		// 								? null
		// 								: new Date(orgCreationDate)
		// 				const lifetimeStart =
		// 						parsedCreationDate && isValid(parsedCreationDate)
		// 								? parsedCreationDate
		// 								: startOfYear(referenceDate)
		// 				const start = startOfDay(lifetimeStart)
		// 				const end = endOfDay(referenceDate)
		// 				return { startDate: start, endDate: end }
		// 		},
		// },
]

const RangeDay: FC<RangeDayProps> = ({
    rangeStart,
    rangeEnd,
    day,
    ...other
}) => {
    const isStart = rangeStart ? isSameDay(day, rangeStart) : false
    const isEnd = rangeEnd ? isSameDay(day, rangeEnd) : false
    const isInRange =
        rangeStart && rangeEnd
            ? isWithinInterval(day, {
                  start: rangeStart < rangeEnd ? rangeStart : rangeEnd,
                  end: rangeStart < rangeEnd ? rangeEnd : rangeStart,
              })
            : false

    return (
        <PickersDay
            {...other}
            day={day}
            selected={isStart || isEnd}
            sx={{
                ...(isInRange && !isStart && !isEnd
                    ? { backgroundColor: 'action.selected', borderRadius: 0 }
                    : {}),
                ...(isStart || isEnd
                    ? {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                      }
                    : {}),
            }}
        />
    )
}

export const DateRangePicker: FC<DateRangePickerProps> = ({
    orgCreationDate,
    startDate,
    endDate,
    onChange,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [activeTab, setActiveTab] = useState(1)
    const [rangeStart, setRangeStart] = useState<Date | null>(startDate)
    const [rangeEnd, setRangeEnd] = useState<Date | null>(endDate)
    const referenceDate = new Date()

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handlePresetClick = (preset: DateRangePreset) => {
        const { startDate, endDate } = preset.getValue(
            new Date(),
            orgCreationDate
        )
        onChange({ startDate, endDate })
        handleClose()
    }

    const handleDayClick = (day: Date) => {
        if (!rangeStart || (rangeStart && rangeEnd)) {
            setRangeStart(day)
            setRangeEnd(null)
            return
        }
        const newStart = day < rangeStart ? day : rangeStart
        const newEnd = day < rangeStart ? rangeStart : day
        setRangeStart(newStart)
        setRangeEnd(newEnd)
    }

    const handleApplyRange = () => {
        if (rangeStart && rangeEnd) {
            onChange({ startDate: rangeStart, endDate: rangeEnd })
            handleClose()
        }
    }

    const open = Boolean(anchorEl)

    const displayValue =
        startDate && endDate
            ? `${format(startDate, 'MMM d, yyyy')} - ` +
              `${format(endDate, 'MMM d, yyyy')}`
            : 'Select Date Range'

    return (
        <Box width="100%">
            <FormControl fullWidth>
                <InputLabel
                    shrink
                    htmlFor="date-range-input"
                    sx={{ fontWeight: 'bold', color: 'text.primary' }}
                >
                    Date:
                </InputLabel>
                <OutlinedInput
                    id="date-range-input"
                    readOnly
                    notched
                    label="Date"
                    value={displayValue}
                    onClick={handleClick}
                    endAdornment={
                        <InputAdornment position="end">
                            <IoCalendarOutline
                                color={colors.chips.PEROLOUS}
                                size={primaryIconSize}
                            />
                        </InputAdornment>
                    }
                    sx={{
                        cursor: 'pointer',
                        '& input': { cursor: 'pointer' },
                    }}
                />
            </FormControl>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: { sx: { width: 350, mt: 1, borderRadius: 2 } },
                }}
            >
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, val) => setActiveTab(val)}
                        variant="fullWidth"
                    >
                        <Tab label="Presets" sx={{ textTransform: 'none' }} />
                        <Tab label="Range" sx={{ textTransform: 'none' }} />
                    </Tabs>
                </Box>

                {activeTab === 0 && (
                    <List sx={{ p: 0 }}>
                        {dateRangePresets.map((preset) => {
                            const { startDate, endDate } = preset.getValue(
                                referenceDate,
                                orgCreationDate
                            )
                            const dateFormat =
                                startDate.getFullYear() ===
                                endDate.getFullYear()
                                    ? 'MMM d'
                                    : 'MMM d, yyyy'

                            return (
                                <ListItem key={preset.label} disablePadding>
                                    <ListItemButton
                                        onClick={() =>
                                            handlePresetClick(preset)
                                        }
                                        sx={{
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <ListItemText primary={preset.label} />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                ml: 2,
                                            }}
                                        >
                                            {isSameDay(startDate, endDate)
                                                ? format(startDate, dateFormat)
                                                : `${format(
                                                      startDate,
                                                      dateFormat
                                                  )} - ${format(
                                                      endDate,
                                                      dateFormat
                                                  )}`}
                                        </Typography>
                                    </ListItemButton>
                                </ListItem>
                            )
                        })}
                    </List>
                )}

                {activeTab === 1 && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Box pt={2}>
                            <Box display="flex" gap={1} sx={{ px: 1, pb: 1 }}>
                                <DateField
                                    label="Start Date"
                                    format="MM/dd/yyyy"
                                    disableFuture
                                    value={rangeStart}
                                    onChange={(value) => {
                                        if (!value || isValid(value)) {
                                            setRangeStart(value)
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                        },
                                    }}
                                />
                                <DateField
                                    label="End Date"
                                    format="MM/dd/yyyy"
                                    disableFuture
                                    value={rangeEnd}
                                    onChange={(value) => {
                                        if (!value || isValid(value)) {
                                            setRangeEnd(value)
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ pl: 1 }}
                            >
                                {!rangeStart
                                    ? 'Select a start date'
                                    : !rangeEnd
                                    ? 'Select an end date'
                                    : `${format(rangeStart, 'MMM d')} – ` +
                                      `${format(rangeEnd, 'MMM d')}`}
                            </Typography>
                            <DateCalendar
                                disableFuture
                                value={rangeEnd ?? rangeStart}
                                onChange={(day) => day && handleDayClick(day)}
                                slots={{ day: RangeDay as any }}
                                slotProps={{
                                    day: { rangeStart, rangeEnd } as any,
                                }}
                            />
                            <Box display="flex" justifyContent="flex-end" p={1}>
                                <Button
                                    variant="contained"
                                    disabled={!rangeStart || !rangeEnd}
                                    onClick={handleApplyRange}
                                >
                                    Apply
                                </Button>
                            </Box>
                        </Box>
                    </LocalizationProvider>
                )}
            </Popover>
        </Box>
    )
}
